import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { withTranslation } from "react-i18next";
import SimpleBar from "simplebar-react";

import { MENU_STRUCTURE } from "../../constants/menu";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useLayout } from "../../hooks/useLayout";
import { useTenant } from "../../hooks/useTenant";
import { MethodologyPaymentModal } from "../Common";
import metodoNeoImg from "../../assets/images/metodoNeo.png";
import neoLogo from "../../assets/images/Neo_Logo_White.svg";
import "./NeoSidebar.css";

const NeoSidebar = (props) => {
  const { canAccess, loading: loadingPermissions } = useAuthorization();
  const { setSidebarOpen } = useLayout();
  const { 
    idTenant, 
    tenantName, 
    planId, 
    hasNeoMethodology, 
    methodologyNeoPrice, 
    refreshTenant,
    user 
  } = useTenant();
  const [searchTerm, setSearchTerm] = useState("");
  const [openMenus, setOpenMenus] = useState([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const location = useLocation();

  const handleLinkClick = () => {
    if (window.innerWidth <= 992) {
      setSidebarOpen(false);
    }
  };

  // Abre os menus cujo subitem corresponde à rota atual na inicialização
  useEffect(() => {
    if (loadingPermissions) return;
    const initialOpen = [];
    MENU_STRUCTURE.forEach((item, index) => {
      if (item.subItems) {
        const hasActiveSub = item.subItems.some(sub => location.pathname.startsWith(sub.path));
        if (hasActiveSub) {
          initialOpen.push(index);
        }
      }
    });
    setOpenMenus(initialOpen);
  }, [loadingPermissions, location.pathname]);

  useEffect(() => {
    // Para garantir que o corpo abra espaço para a sidebar expandida
    document.body.classList.add("neo-sidebar-expanded");
    return () => document.body.classList.remove("neo-sidebar-expanded");
  }, []);

  const toggleMenu = (index, e) => {
    e.preventDefault();
    setOpenMenus((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const isActive = (item) => {
    if (item.path) {
      return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
    }
    return false;
  };

  const getActiveSubItem = (subItems) => {
    let activeSubItem = null;
    let maxLength = 0;
    subItems.forEach((sub) => {
      if (location.pathname === sub.path || location.pathname.startsWith(sub.path + "/")) {
        if (sub.path.length > maxLength) {
          maxLength = sub.path.length;
          activeSubItem = sub;
        }
      }
    });
    return activeSubItem;
  };

  const renderMenu = () => {
    if (loadingPermissions) return null;

    return MENU_STRUCTURE.map((item, index) => {
      // Controle de Permissão Geral
      if (!canAccess({ feature: item.feature, permission: item.permission, permissionsAny: item.permissionsAny || item.permissions })) return null;

      // Lógica de Filtro de Busca com normalização de acentos
      const normalizeStr = (str) => str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";
      const searchLower = normalizeStr(searchTerm);
      const matchLabel = normalizeStr(props.t(item.label)).includes(searchLower);

      let visibleSubItems = [];
      if (item.subItems) {
        visibleSubItems = item.subItems.filter(sub => {
          const hasAccess = canAccess({ feature: sub.feature, permission: sub.permission, permissionsAny: sub.permissionsAny || sub.permissions });
          const matchesSearch = normalizeStr(props.t(sub.label)).includes(searchLower);
          return hasAccess && (matchesSearch || matchLabel);
        });
      }

      // Ocultar se a busca estiver ativa e não houver match
      if (searchTerm && !item.isHeader && !matchLabel && visibleSubItems.length === 0) {
        return null;
      }

      if (item.isHeader) {
        if (searchTerm) return null;
        return <li key={index} className="neo-menu-title">{props.t(item.label)}</li>;
      }

      // Renderização com Subitens (Expansível)
      if (item.subItems) {
        if (visibleSubItems.length === 0) return null;

        // Se estiver buscando e achar um filho, abre o menu automaticamente
        const isOpen = openMenus.includes(index) || (searchTerm && visibleSubItems.length > 0);

        // Determina o filho ativo real (evitando falsos positivos do Operacional)
        const activeSubItem = getActiveSubItem(visibleSubItems);
        const hasActiveChild = activeSubItem !== null;

        return (
          <li key={index} className={`neo-sidebar-item ${isOpen ? 'open' : ''} ${hasActiveChild ? 'has-active-child' : ''}`}>
            <a href="/#" onClick={(e) => toggleMenu(index, e)} className="neo-sidebar-link">
              <div className="neo-link-content">
                <i className={`${item.icon} neo-item-icon`}></i>
                <span className="neo-item-label">{props.t(item.label)}</span>
              </div>
              <i className={`mdi mdi-chevron-${isOpen ? 'up' : 'down'} neo-chevron`}></i>
            </a>

            {isOpen && (
              <ul className="neo-sidebar-submenu">
                {visibleSubItems.map((subItem, subIndex) => (
                  <li key={subIndex} className="neo-submenu-item">
                    <Link to={subItem.path} className={`neo-submenu-link ${subItem === activeSubItem ? 'active' : ''}`} onClick={handleLinkClick}>
                      {props.t(subItem.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      }

      // Renderização Item Simples
      const currentActive = isActive(item);
      return (
        <li key={index} className={`neo-sidebar-item`}>
          <Link to={item.path} className={`neo-sidebar-link ${currentActive ? 'active' : ''}`} onClick={handleLinkClick}>
            <div className="neo-link-content">
              <i className={`${item.icon} neo-item-icon`}></i>
              <span className="neo-item-label">{props.t(item.label)}</span>
            </div>
          </Link>
        </li>
      );
    });
  };

  const isAuthorizedToBuy = user?.role === "owner" || user?.role === "admin" || user?.role === "super_admin";
  const hasAccessToMethodology = hasNeoMethodology === true || planId === "neo";
  const showPromoCard = !loadingPermissions && isAuthorizedToBuy && !hasAccessToMethodology && idTenant;

  return (
    <div className="neo-sidebar-container">
      {/* Logo / Branding */}
      <div className="neo-sidebar-brand">
        <Link to="/dashboard">
          <img src={neoLogo} alt="NeoApp" className="neo-sidebar-logo" />
        </Link>
      </div>

      {/* Search Box */}
      <div className="neo-sidebar-search">
        <div className="search-box position-relative">
          <input
            type="text"
            className="form-control rounded border-0"
            placeholder="Pesquisar no menu"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <i className="mdi mdi-magnify neo-search-icon"></i>
        </div>
      </div>

      <SimpleBar style={{ flex: 1, minHeight: 0 }}>
        <ul className="neo-sidebar-menu">
          {renderMenu()}
        </ul>
      </SimpleBar>

      {/* Card Promocional Metodologia NEO */}
      {showPromoCard && (
        <div className="neo-sidebar-promo-card m-3 p-3 rounded" style={{
            background: "linear-gradient(135deg, #2c1810 0%, #1a0f0a 100%)",
            border: "1px solid rgba(234, 88, 12, 0.25)",
            boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.3)",
            position: "relative",
            overflow: "hidden"
        }}>
            <div style={{
                height: "85px",
                backgroundImage: `url(${metodoNeoImg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: "6px",
                marginBottom: "12px",
                border: "1px solid rgba(255,255,255,0.05)"
            }} />
            <h6 className="fw-bold text-white mb-1 neo-font-outfit" style={{ fontSize: "0.85rem", letterSpacing: "0.3px" }}>Metodologia NEO</h6>
            <p className="text-muted mb-3" style={{ fontSize: "0.7rem", lineHeight: "1.3", color: "#bda89b !important" }}>
                Adquira o planejamento completo de atividades, níveis e objetivos pedagógicos.
            </p>
            <div className="d-flex align-items-center justify-content-between">
                <span className="fw-bold" style={{ color: "#ea580c", fontSize: "0.9rem" }}>
                    R$ {methodologyNeoPrice}
                </span>
                <button 
                    onClick={() => setPaymentModalOpen(true)}
                    className="btn btn-sm" 
                    style={{
                        background: "#ea580c",
                        border: "none",
                        color: "#fff",
                        fontWeight: "700",
                        fontSize: "0.75rem",
                        padding: "4px 10px",
                        borderRadius: "4px",
                        transition: "all 0.2s"
                    }}
                >
                    Adquirir
                </button>
            </div>
        </div>
      )}

      {/* Modal de Pagamento da Metodologia */}
      {showPromoCard && (
        <MethodologyPaymentModal 
            isOpen={paymentModalOpen}
            toggle={() => setPaymentModalOpen(!paymentModalOpen)}
            idTenant={idTenant}
            price={methodologyNeoPrice}
            email={user?.email || ""}
            name={tenantName || ""}
            onPaymentComplete={() => {
                setPaymentModalOpen(false);
                refreshTenant();
            }}
        />
      )}

      {/* Footer Version Info */}
      {!loadingPermissions && (
        <div className="neo-sidebar-footer">
          Versão: 2026.04.20.3
        </div>
      )}
    </div>
  );
};

NeoSidebar.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(NeoSidebar);
